// src/components/security/PeerResetCard.tsx
/**
 * The two-admin answer to a lost phone and lost recovery codes.
 *
 * `POST /admin/security/admins/{id}/reset-mfa` clears the target's enrolment and ends their sessions,
 * so they fall back through first-time setup on their next login. The caller must already be
 * step-up verified — which is the whole design: the way back in for one admin is the OTHER admin's
 * working second factor, never a self-service reset.
 *
 * Which is exactly why an admin's OWN id is refused here, before the request is made. The server
 * would happily accept it — the endpoint takes any user id — and the result would be an admin who
 * has just deleted the second factor they were verified with, on a page that then locks itself. The
 * refusal names the two real ways out instead.
 */
import React, { useState } from 'react';
import { useSelector } from 'react-redux';
import { errText, httpStatus } from '../../api/errors';
import { resetAdminMfa } from '../../api/securityApi';
import { RootState } from '../../store/store';
import LockedCard from './LockedCard';
import {
    Banner,
    Card,
    CardTitle,
    Danger,
    Field,
    Hint,
    Input,
    Label,
    Row,
    WarnBox,
} from './ui';

const TITLE = 'Núdzový reset druhého správcu';

const CONFIRM_PHRASE = 'RESET';

const OWN_ID_REFUSED =
    'Vlastné dvojstupňové overenie sa takto zrušiť nedá — použite záložný kód alebo požiadajte ' +
    'druhého správcu.';
const NOT_FOUND = 'Účet s týmto ID neexistuje.';
const RESET_FAILED = 'Reset dvojstupňového overenia sa nepodaril.';

interface PeerResetCardProps {
    enabled: boolean;
    sessionVerified: boolean;
}

/** Split for the same reason as `SessionsCard`: the unlocked half uses hooks, and a hook behind an
 *  early return is a hook that only runs on some renders. */
const PeerResetCard: React.FC<PeerResetCardProps> = ({ enabled, sessionVerified }) => {
    if (!enabled || !sessionVerified) {
        return <LockedCard title={TITLE} enabled={enabled} />;
    }
    return <UnlockedPeerReset />;
};

const UnlockedPeerReset: React.FC = () => {
    const currentUser = useSelector((state: RootState) => state.auth.user);

    const [accountId, setAccountId] = useState('');
    const [phrase, setPhrase] = useState('');
    const [busy, setBusy] = useState(false);
    const [error, setError] = useState<string | null>(null);
    const [notice, setNotice] = useState<string | null>(null);

    // A text field constrained to digits rather than `type="number"`: a number input reports an
    // empty string for anything it dislikes, which loses what the reader typed, and its spinner is
    // meaningless for an account id.
    const targetId = accountId === '' ? NaN : parseInt(accountId, 10);
    const idValid = !isNaN(targetId) && targetId > 0;
    const armed = idValid && phrase.trim().toUpperCase() === CONFIRM_PHRASE;

    const submit = async () => {
        if (busy || !armed) {
            return;
        }
        setError(null);
        setNotice(null);

        if (currentUser != null && targetId === currentUser.id) {
            setError(OWN_ID_REFUSED);
            return;
        }

        setBusy(true);
        try {
            await resetAdminMfa(targetId);
            setNotice(
                'Dvojstupňové overenie účtu #' +
                    targetId +
                    ' bolo zrušené a jeho relácie boli ukončené. Pri najbližšom prihlásení si ho ' +
                    'nastaví odznova.'
            );
            setAccountId('');
            setPhrase('');
        } catch (err) {
            // 404 is the one status worth its own sentence: it is almost always a mistyped id, and
            // the generic message would send the reader looking for a server fault.
            // Never log the error object — it carries the request headers, and those carry the token.
            setError(httpStatus(err) === 404 ? NOT_FOUND : errText(err, RESET_FAILED));
        } finally {
            setBusy(false);
        }
    };

    return (
        <Card>
            <CardTitle>{TITLE}</CardTitle>

            {error && <Banner>{error}</Banner>}
            {notice && <Banner $ok>{notice}</Banner>}

            <Hint style={{ margin: '0 0 14px' }}>
                Ak druhý správca stratil telefón aj záložné kódy, tu mu zrušíte dvojstupňové overenie
                — pri najbližšom prihlásení si ho nastaví odznova. ID účtu nájdete v stĺpci Používateľ
                v tabuľke relácií vyššie.
            </Hint>

            <WarnBox>
                Zruší to jeho záznam v autentifikačnej aplikácii aj všetky jeho záložné kódy a odhlási
                ho zo všetkých zariadení.
            </WarnBox>

            <Field>
                <Label htmlFor="peer-reset-id">ID účtu správcu</Label>
                <Input
                    id="peer-reset-id"
                    value={accountId}
                    onChange={(e) => setAccountId(e.target.value.replace(/\D+/g, ''))}
                    inputMode="numeric"
                    pattern="[0-9]*"
                    autoComplete="off"
                    placeholder="napr. 42"
                    style={{ maxWidth: 180 }}
                />
            </Field>

            <Field>
                <Label htmlFor="peer-reset-confirm">
                    Na potvrdenie napíšte {CONFIRM_PHRASE}
                </Label>
                <Input
                    id="peer-reset-confirm"
                    value={phrase}
                    onChange={(e) => setPhrase(e.target.value)}
                    autoComplete="off"
                    placeholder={CONFIRM_PHRASE}
                    style={{ maxWidth: 180 }}
                />
            </Field>

            <Row>
                <Danger type="button" disabled={busy || !armed} onClick={submit}>
                    {busy ? 'Ruším…' : 'Zrušiť 2FA a odhlásiť účet'}
                </Danger>
            </Row>
        </Card>
    );
};

export default PeerResetCard;
